/**
 * Утилиты для оптимизации изображений
 *
 * Сейчас реальной оптимизации нет — функции возвращают оригинальный URL.
 * Это потому что Supabase Image Transformations доступны только на Pro плане.
 * Resize картинок происходит на клиенте при загрузке.
 *
 * Документация: https://supabase.com/docs/guides/storage/serving/image-transformations
 */

/**
 * Размеры для разных типов изображений
 */
export const IMAGE_SIZES = {
  // Аватары
  AVATAR_THUMB: { width: 80, height: 80 },
  AVATAR_MEDIUM: { width: 150, height: 150 },
  AVATAR_LARGE: { width: 300, height: 300 },
  
  // Обложки вишлистов
  WISHLIST_COVER_THUMB: { width: 300, height: 400 }, // aspect 3:4
  WISHLIST_COVER_MEDIUM: { width: 600, height: 800 },
  
  // Фото желаний
  ITEM_PHOTO_THUMB: { width: 200, height: 200 },
  ITEM_PHOTO_CARD: { width: 400, height: 400 },
  ITEM_PHOTO_FULL: { width: 1200, height: 1200 },
} as const;

/**
 * Опции для оптимизации изображения (используются хелперами, передаются
 * в getOptimizedImageUrl но сейчас игнорируются — см. комментарий ниже)
 */
export interface ImageTransformOptions {
  /** Ширина в пикселях */
  width?: number;
  /** Высота в пикселях */
  height?: number;
  /** Качество сжатия (1-100) */
  quality?: number;
  /** Формат изображения */
  format?: 'webp' | 'jpeg' | 'png';
}

/**
 * Создаёт оптимизированный URL для изображения из Supabase Storage
 *
 * ⚠️ ВАЖНО: Supabase Image Transformations это **платная фича** Pro плана и выше.
 * На free tier эндпоинт /storage/v1/render/image/public/ возвращает 400/404,
 * картинки не грузятся.
 *
 * Поэтому функция сейчас работает как **no-op**: просто возвращает оригинальный
 * URL без трансформаций. Resize картинок происходит на клиенте при загрузке
 * (uploadWishlistItemPhoto делает client-side resize до ~1500px), так что
 * без серверных трансформаций мы ничего критичного не теряем.
 *
 * Если в будущем перейдёшь на Pro план — раскомментируй блок ниже и удали
 * `return url`. Тогда заработают полноценные трансформации.
 *
 * Документация: https://supabase.com/docs/guides/storage/serving/image-transformations
 */
export function getOptimizedImageUrl(
  url: string | null | undefined,
  // Параметр сохраняется в сигнатуре для совместимости и будущей реализации
  // (см. закомментированный блок ниже — full implementation для Pro плана).
  // Префикс _ — сигнал "знаем что не используется сейчас".
  _options: ImageTransformOptions = {}
): string | undefined {
  if (!url) return undefined;
  return url;

  // ─── Полноценная реализация для Pro плана (закомментирована) ──────────────
  //
  // if (!isSupabaseStorageUrl(url)) return url;
  //
  // const hasAnyTransform = options.width || options.height || options.quality || options.format;
  // if (!hasAnyTransform) return url;
  //
  // try {
  //   const urlObj = new URL(url);
  //
  //   // Трансформации работают на отдельном эндпоинте /render/image/public/
  //   urlObj.pathname = urlObj.pathname.replace(
  //     '/storage/v1/object/public/',
  //     '/storage/v1/render/image/public/',
  //   );
  //
  //   const params = new URLSearchParams();
  //   if (options.width) params.set('width', options.width.toString());
  //   if (options.height) params.set('height', options.height.toString());
  //   if (options.quality) params.set('quality', Math.min(100, Math.max(1, options.quality)).toString());
  //   if (options.format) params.set('format', options.format);
  //
  //   urlObj.search = params.toString();
  //   return urlObj.toString();
  // } catch (error) {
  //   handleApiError(error, { consolePrefix: `Ошибка оптимизации изображения (URL: ${url})` });
  //   return url;
  // }
}

/**
 * Хелперы для часто используемых случаев
 */

/** Оптимизированный URL для аватара (маленький) */
export function getAvatarUrl(url: string | null | undefined): string | undefined {
  return getOptimizedImageUrl(url, {
    ...IMAGE_SIZES.AVATAR_THUMB,
    quality: 85,
    format: 'webp',
  });
}

/** Оптимизированный URL для большого аватара */
export function getAvatarLargeUrl(url: string | null | undefined): string | undefined {
  return getOptimizedImageUrl(url, {
    ...IMAGE_SIZES.AVATAR_LARGE,
    quality: 90,
    format: 'webp',
  });
}

/** Оптимизированный URL для обложки вишлиста (thumbnail) */
export function getWishlistCoverUrl(url: string | null | undefined): string | undefined {
  return getOptimizedImageUrl(url, {
    ...IMAGE_SIZES.WISHLIST_COVER_THUMB,
    quality: 85,
    format: 'webp',
  });
}

/** Оптимизированный URL для фото желания (карточка) */
export function getItemPhotoUrl(url: string | null | undefined): string | undefined {
  return getOptimizedImageUrl(url, {
    ...IMAGE_SIZES.ITEM_PHOTO_CARD,
    quality: 85,
    format: 'webp',
  });
}

/** Оптимизированный URL для фото желания (thumbnail для сетки) */
export function getItemPhotoThumbUrl(url: string | null | undefined): string | undefined {
  return getOptimizedImageUrl(url, {
    ...IMAGE_SIZES.ITEM_PHOTO_THUMB,
    quality: 80,
    format: 'webp',
  });
}